import type { HolidayRuleInterface, ScheduleInterface } from "@swivl-mono/types";

import type { ScheduleScopeKind } from "./InheritancePill";

/**
 * One of three editable scopes in the Hours page: org default, group default,
 * or location override. Mirrors `LocationVariableEntity`'s scope shape.
 */
export interface PageScope {
  kind: ScheduleScopeKind;
  /** Set when kind === "group" or "location" (and the location belongs to a group). */
  groupId?: number;
  /** Set when kind === "location". */
  locationId?: number;
}

/** Where a row lives in the (orgId, groupId?, locationId?) cascade. */
export function rowScopeKind(row: { groupId: number | null; locationId: number | null }): ScheduleScopeKind {
  if (row.locationId != null) return "location";
  if (row.groupId != null) return "group";
  return "org";
}

/** True iff the row lives at the same scope as the page. */
export function isRowOwnedAtScope(row: { groupId: number | null; locationId: number | null }, scope: PageScope): boolean {
  if (scope.kind === "location") return row.locationId === scope.locationId;
  if (scope.kind === "group") return row.locationId == null && row.groupId === scope.groupId;
  return row.locationId == null && row.groupId == null;
}

/**
 * Pick the row that "wins" the cascade for the page's view: the most-specific
 * row that is at-or-broader than the current scope. At org scope only org
 * rows are visible; at group scope, group + org; at location scope, all
 * three.
 */
export function pickEffectiveSchedule(rows: ScheduleInterface[], scope: PageScope, slug: string): ScheduleInterface | null {
  const candidates = rows.filter((r) => r.slug === slug && r.isActive);
  if (scope.kind === "location") {
    const own = candidates.find((r) => r.locationId === scope.locationId);
    if (own) return own;
    if (scope.groupId != null) {
      const grp = candidates.find((r) => r.locationId == null && r.groupId === scope.groupId);
      if (grp) return grp;
    }
    return candidates.find((r) => r.locationId == null && r.groupId == null) ?? null;
  }
  if (scope.kind === "group") {
    const own = candidates.find((r) => r.locationId == null && r.groupId === scope.groupId);
    if (own) return own;
    return candidates.find((r) => r.locationId == null && r.groupId == null) ?? null;
  }
  return candidates.find((r) => r.locationId == null && r.groupId == null) ?? null;
}

/**
 * Holiday rules visible from a given scope: all rules at-or-broader. The PM
 * UI shows them all and badges each with its provenance.
 */
export function visibleHolidays(rules: HolidayRuleInterface[], scope: PageScope): HolidayRuleInterface[] {
  return rules.filter((r) => {
    if (r.locationId != null) return scope.kind === "location" && r.locationId === scope.locationId;
    if (r.groupId != null) {
      if (scope.kind === "org") return false;
      return r.groupId === scope.groupId;
    }
    return true; // org-scope rule, always visible
  });
}
